import { Controller } from 'stimulus'

type Batch = { html: string; count: number; remaining: number }

// Partnerstimmen-Grid auf /referenzen: laedt die Karten in Chargen nach
// (bei ~255 Karten waeren das sonst ~750 KB HTML plus ebenso viele Bilder)
// und laedt den Detail-Inhalt fuers Modal per fetch.
export default class extends Controller {
  declare filterTargets: HTMLButtonElement[]
  declare taglineTarget: HTMLElement
  declare countTarget: HTMLElement
  declare emptyTarget: HTMLElement
  declare gridTarget: HTMLElement
  declare showMoreContainerTarget: HTMLElement
  declare showMoreBtnTarget: HTMLButtonElement
  declare modalBodyTarget: HTMLElement
  declare hasEmptyTarget: boolean
  declare hasTaglineTarget: boolean
  declare hasCountTarget: boolean
  declare hasShowMoreContainerTarget: boolean
  declare hasShowMoreBtnTarget: boolean
  declare hasModalBodyTarget: boolean
  declare hasGridTarget: boolean

  static targets = [
    'filter', 'tagline', 'count', 'empty', 'grid',
    'showMoreContainer', 'showMoreBtn', 'modalBody'
  ]

  private spinnerHtml = ''
  private currentBranche = 'all'
  private batchSize = 24

  connect() {
    if (this.hasModalBodyTarget) this.spinnerHtml = this.modalBodyTarget.innerHTML
    if (this.hasShowMoreBtnTarget) {
      this.batchSize = parseInt(this.showMoreBtnTarget.dataset.offset || '24', 10)
    }

    const modal = document.getElementById('partnerstimme-modal')
    modal?.addEventListener('hidden.bs.modal', () => {
      if (this.hasModalBodyTarget) this.modalBodyTarget.innerHTML = this.spinnerHtml
    })

    const branche = new URLSearchParams(window.location.search).get('branche')
    if (branche && branche !== 'all') this.applyFilter(branche)
  }

  filter(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    this.applyFilter(button.dataset.portalType || 'all')
  }

  showMore(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const offset = parseInt(button.dataset.offset || String(this.batchSize), 10)

    button.disabled = true
    this.fetchBatch(offset).then(data => {
      button.disabled = false
      if (!data || !this.hasGridTarget) return

      this.gridTarget.insertAdjacentHTML('beforeend', data.html)
      this.updateShowMore(offset + this.batchSize, data.remaining)
    })
  }

  open(event: Event) {
    const username = (event.currentTarget as HTMLElement).dataset.username
    if (!username || !this.hasModalBodyTarget) return

    this.modalBodyTarget.innerHTML = this.spinnerHtml

    fetch(`/referenzen/partnerstimme/${encodeURIComponent(username)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.html) this.modalBodyTarget.innerHTML = data.html
      })
      .catch(() => {})
  }

  private applyFilter(branche: string) {
    this.currentBranche = branche

    this.filterTargets.forEach(button => {
      const active = (button.dataset.portalType || '') === branche
      button.classList.toggle('btn-primary', active)
      button.classList.toggle('btn-outline-primary', !active)
      if (active && this.hasTaglineTarget) {
        this.taglineTarget.textContent = button.dataset.tagline || ''
      }
    })

    if (!this.hasGridTarget) return

    this.gridTarget.innerHTML = ''
    this.fetchBatch(0).then(data => {
      if (!data) return

      this.gridTarget.innerHTML = data.html
      this.updateCount(data.count)
      this.updateShowMore(this.batchSize, data.remaining)

      if (this.hasEmptyTarget) {
        this.emptyTarget.classList.toggle('d-none', data.count > 0)
      }
    })

    this.updateUrl(branche)
  }

  private fetchBatch(offset: number): Promise<Batch | null> {
    const params = new URLSearchParams({ offset: String(offset) })
    if (this.currentBranche !== 'all') params.set('branche', this.currentBranche)

    return fetch(`/referenzen/partnerstimmen?${params.toString()}`)
      .then(res => (res.ok ? res.json() : null))
      .catch(() => null)
  }

  private updateShowMore(nextOffset: number, remaining: number) {
    if (!this.hasShowMoreContainerTarget || !this.hasShowMoreBtnTarget) return

    if (remaining > 0) {
      this.showMoreContainerTarget.classList.remove('d-none')
      this.showMoreBtnTarget.dataset.offset = String(nextOffset)
      const label = this.showMoreBtnTarget.dataset.label || ''
      this.showMoreBtnTarget.textContent = label.replace('%{count}', String(remaining))
    } else {
      this.showMoreContainerTarget.classList.add('d-none')
    }
  }

  private updateCount(count: number) {
    if (!this.hasCountTarget) return

    const ds = this.countTarget.dataset
    const template = count === 1 ? (ds.templateOne || '') : (ds.templateOther || '')
    this.countTarget.textContent = template.replace('%{count}', String(count))
  }

  private updateUrl(branche: string) {
    const params = new URLSearchParams(window.location.search)
    if (branche === 'all') {
      params.delete('branche')
    } else {
      params.set('branche', branche)
    }
    const qs = params.toString()
    window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname)
  }
}
